import { createSlice } from "@reduxjs/toolkit";
import {
  fetchSummaryData,
  fetchCompanyList,
  fetchBranchList,
  fetchProductList,
  fetchAdditionalInfoFields,
  exportData,
} from "./scan-track-summary-data.action";

const today = new Date();
const twoWeeksAgo = new Date();
twoWeeksAgo.setDate(today.getDate() - 14);
const formatDate = (date) => date.toISOString().split("T")[0];

const initialState = {
  data: [],
  companies: [],
  branches: [],
  products: [],
  additionalInfoFields: [],
  additionalInfoMeta: [], // Metadata for displayed additional info columns
  page: 0,
  itemPerPage: 25,
  totalCount: 0,
  loading: {
    data: false,
    companies: false,
    branches: false,
    products: false,
    additionalInfoFields: false,
  },
  error: {
    data: null,
    companies: null,
    branches: null,
    products: null,
    additionalInfoFields: null,
  },
  filters: {
    company: "",
    branch: "ALL",
    product: "ALL",
    filterMode: "By Month",
    year: today.getFullYear().toString(),
    month: today.toLocaleString("default", { month: "long" }),
    startDate: formatDate(twoWeeksAgo),
    endDate: formatDate(today),
    isDateRangeValid: true,
  },
  isExporting: false,
};
const scanTrackSummarySlice = createSlice({
  name: "scanTrackSummary",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.page = 0;
    },
    setItemPerPage: (state, action) => {
      state.itemPerPage = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    resetAdditionalInfoFields: (state) => {
      state.additionalInfoFields = [];
      state.error.additionalInfoFields = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSummaryData.pending, (state) => {
        state.loading.data = true;
        state.error.data = null;
      })
      .addCase(fetchSummaryData.fulfilled, (state, action) => {
        const { data, total, additionalInfoMeta } = action.payload;
        state.loading.data = false;
        state.data = data;
        state.totalCount = total || 0;
        state.additionalInfoMeta = additionalInfoMeta || [];
      })
      .addCase(fetchSummaryData.rejected, (state, action) => {
        state.loading.data = false;
        state.error.data = action.error.message;
      })

      .addCase(fetchCompanyList.pending, (state) => {
        state.loading.companies = true;
        state.error.companies = null;
      })
      .addCase(fetchCompanyList.fulfilled, (state, action) => {
        state.loading.companies = false;
        state.companies = action.payload;
      })
      .addCase(fetchCompanyList.rejected, (state, action) => {
        state.loading.companies = false;
        state.error.companies = action.error.message;
      })

      .addCase(fetchBranchList.pending, (state) => {
        state.loading.branches = true;
        state.error.branches = null;
      })
      .addCase(fetchBranchList.fulfilled, (state, action) => {
        state.loading.branches = false;
        state.branches = action.payload;
      })
      .addCase(fetchBranchList.rejected, (state, action) => {
        state.loading.branches = false;
        state.error.branches = action.error.message;
      })

      .addCase(fetchProductList.pending, (state) => {
        state.loading.products = true;
        state.error.products = null;
      })
      .addCase(fetchProductList.fulfilled, (state, action) => {
        state.loading.products = false;
        state.products = action.payload;
      })
      .addCase(fetchProductList.rejected, (state, action) => {
        state.loading.products = false;
        state.error.products = action.error.message;
      })

      .addCase(fetchAdditionalInfoFields.pending, (state) => {
        state.loading.additionalInfoFields = true;
        state.error.additionalInfoFields = null;
      })
      .addCase(fetchAdditionalInfoFields.fulfilled, (state, action) => {
        state.loading.additionalInfoFields = false;
        state.additionalInfoFields = action.payload;
      })
      .addCase(fetchAdditionalInfoFields.rejected, (state, action) => {
        state.loading.additionalInfoFields = false;
        state.error.additionalInfoFields = action.error.message;
      })

      .addCase(exportData.pending, (state) => {
        state.isExporting = true;
      })
      .addCase(exportData.fulfilled, (state) => {
        state.isExporting = false;
      })
      .addCase(exportData.rejected, (state) => {
        state.isExporting = false;
      });
  },
});

export const { setFilters, setPage, setItemPerPage, resetAdditionalInfoFields } =
  scanTrackSummarySlice.actions;
export default scanTrackSummarySlice.reducer;
